import React from 'react';

export default function About() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #6bbcff, #a979ff)',
      padding: '3rem 1rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    }}>
      <div style={{
        maxWidth: '900px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '2.5rem',
        fontFamily: 'Inter, system-ui, sans-serif',
        lineHeight: 1.7,
        color: '#333',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
      }}>
        <h1 style={{ 
          fontSize: 'clamp(2rem, 5vw, 2.5rem)', 
          marginBottom: '1.5rem', 
          color: '#222' 
        }}>About MoodTune</h1>
        <h2 style={{ 
          fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)', 
          marginBottom: '1.5rem', 
          color: '#444', 
          fontWeight: '400' 
        }}>More than a playlist generator — your AI-powered music companion</h2>

        <p>MoodTune is built on a simple belief: music should meet you where you are. Nostalgic, electric, peaceful, or beautifully undefined — MoodTune listens, understands, and responds.</p>

        <p>Just describe your mood — typed or spoken — and MoodTune translates it into emotional themes, sonic textures, and genre blends that craft the perfect playlist. But it doesn't stop there: every playlist comes with a warm, handwritten-style companion note — styled with Caveat font and typing animations — so you feel seen and understood.</p>

        <br />

        <p>Every track tells a story. Every playlist whispers:</p>
        <p style={{ 
          fontStyle: 'italic', 
          textAlign: 'center', 
          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', 
          margin: '1rem 0' 
        }}>"I see you."</p>
        <p>And every note reminds you:</p>
        <p style={{ 
          fontStyle: 'italic', 
          textAlign: 'center', 
          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', 
          margin: '1rem 0' 
        }}>"You're not alone in this feeling."</p>

        <p>With MoodTune's anti-repetition intelligence, you'll never hear the same song twice. Personalized tracking ensures your musical journey stays fresh and uniquely yours.</p>

        <p>Whether you're journaling in a thunderstorm, dancing solo in your kitchen, or wandering city streets lost in thought — MoodTune finds the soundtrack for it all.</p>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
          color: '#222', 
          marginBottom: '1rem' 
        }}>The Origin</h2>

        <p>Hi, I'm <strong>Cedric Xu</strong> — UC Berkeley student, music lover, and builder.</p>

        <p>MoodTune was born on a bike ride along the Bay Trail — golden sunset, cool wind, and a feeling I couldn't name. I wanted music that matched it, but nothing I searched for fit.</p>

        <p>So I asked myself: <em>What if I could just describe how I feel — and something would understand?</em></p>
        <p>That thought became this project. That feeling became MoodTune.</p>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
          color: '#222', 
          marginBottom: '1rem' 
        }}>Tech Behind the Magic</h2>
        
        <p>MoodTune combines cutting-edge AI with thoughtful engineering and design:</p>
        
        <ul style={{ paddingLeft: '1.25rem', marginBottom: '1.5rem' }}>
          <li><strong>Frontend:</strong> React + smooth animations, real-time progress bars, persistent state</li>
          <li><strong>Backend:</strong> Node.js + Express with modular, well-documented architecture</li>
          <li><strong>Database:</strong> PostgreSQL with normalized schema, junction tables, views, triggers, indexing</li>
          <li><strong>AI:</strong> OpenAI GPT-3.5-turbo & GPT-4o-mini for mood analysis</li>
          <li><strong>Music:</strong> Spotify API with OAuth integration for instant playlist creation</li>
          <li><strong>Voice Input:</strong> Web Speech API + custom error handling</li>
          <li><strong>Sound Visualization:</strong> Web Audio API for real-time waveforms</li>
          <li><strong>Anti-Repetition System:</strong> User-specific song tracking (50-song history for logged-in users, 2-hour sessions for guests)</li>
          <li><strong>Authentication:</strong> Modal login/logout popups with JWT handling</li>
          <li><strong>Performance:</strong> Server-Sent Events for real-time progress + companion note delivery</li>
          <li><strong>Responsive UI:</strong> Mobile-first layouts, adaptive scaling, touch-friendly interactions</li>
          <li>And a whole lot of creativity, empathy, and attention to detail. ✨</li>
        </ul>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
          color: '#222', 
          marginBottom: '1rem' 
        }}>How It Works</h2>
        
        <ul style={{ paddingLeft: '1.25rem', marginBottom: '1.5rem' }}>
          <li><strong>Speak or Type</strong> → Tell MoodTune how you feel (with real-time audio visualization)</li>
          <li><strong>Analyze</strong> → AI interprets tone, words, and sentiment</li>
          <li><strong>Personal Note</strong> → Receive a handwritten-style companion message instantly</li>
          <li><strong>Translate</strong> → Emotions mapped to genres, textures, and themes</li>
          <li><strong>Playlist</strong> → Enjoy 6+ unique songs, with Spotify integration</li>
          <li><strong>Manage</strong> → Delete, expand, and request more songs endlessly</li>
          <li><strong>Export</strong> → Save your playlist directly to Spotify</li>
        </ul>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
          color: '#222', 
          marginBottom: '1rem' 
        }}>What Makes MoodTune Special</h2>
        
        <ul style={{ paddingLeft: '1.25rem', marginBottom: '1.5rem' }}>
          <li><strong>Companion Notes:</strong> Warm, handwritten messages with Caveat font + typing animation</li>
          <li><strong>Real-Time Audio Visualization:</strong> Watch your voice form sound waves on screen</li>
          <li><strong>Anti-Repetition Intelligence:</strong> Personalized tracking prevents repeats</li>
          <li><strong>Unlimited Expansion:</strong> Add as many songs as you want</li>
          <li><strong>Artist Requests:</strong> Ask for specific artists — get enhanced recommendations</li>
          <li><strong>Seamless UX:</strong> Hover/tap to manage tracks, fluid animations, and persistent state</li>
          <li><strong>Spotify Integration:</strong> Save directly to your Spotify library</li>
          <li><strong>Mobile-First:</strong> Optimized for any screen with touch-friendly interactions</li>
        </ul>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
          color: '#222', 
          marginBottom: '1rem' 
        }}>Let's Connect</h2>
        <p>Want to collaborate, share feedback, or just say hi?</p>
        <p>
          📬 <strong>Email</strong>: <a 
            href="mailto:cedrichxu@gmail.com" 
            style={{ 
              color: '#000', 
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              textDecorationColor: '#d1d5db',
              transition: 'all 0.2s ease'
            }}
          >
            cedrichxu@gmail.com
          </a><br />
          💻 <strong>GitHub</strong>: <a 
            href="https://github.com/cedricxu312" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#000', 
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              textDecorationColor: '#d1d5db',
              transition: 'all 0.2s ease'
            }}
          >
            github.com/cedricxu312
          </a>
        </p>

        <hr style={{ margin: '2.5rem 0', borderColor: '#eee' }} />

        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
          textAlign: 'center',
          fontStyle: 'italic',
          color: '#444'
        }}>
          Welcome to MoodTune. Let's soundtrack your feelings—one moment at a time.
        </p>
      </div>
    </div>
  );
}
